import React from 'react';
import { validatePassword, getPasswordErrorMessage } from '@/utils/security';

interface PasswordStrengthMeterProps {
  password: string;
  showRequirements?: boolean;
  className?: string;
}

export function PasswordStrengthMeter({ 
  password, 
  showRequirements = true, 
  className = "" 
}: PasswordStrengthMeterProps) {
  const getStrengthLevel = () => {
    if (!password) return { level: 0, label: '', color: 'bg-gray-200' };
    
    let score = 0;
    const requirements = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      numbers: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };
    
    Object.values(requirements).forEach(met => {
      if (met) score++;
    });
    
    if (score <= 1) return { level: score, label: 'Muito Fraca', color: 'bg-red-500' };
    if (score <= 2) return { level: score, label: 'Fraca', color: 'bg-orange-500' };
    if (score <= 3) return { level: score, label: 'Média', color: 'bg-yellow-500' };
    if (score <= 4) return { level: score, label: 'Forte', color: 'bg-blue-500' };
    return { level: score, label: 'Muito Forte', color: 'bg-green-500' };
  };
  
  const { level, label, color } = getStrengthLevel();
  const isStrong = validatePassword(password);
  
  return (
    <div className={`space-y-2 ${className}`}>
      {/* Barra de força */}
      <div className="w-full">
        <div className="flex justify-between text-xs text-gray-600 mb-1">
          <span>Força da senha:</span>
          <span className={isStrong ? 'text-green-600 font-medium' : 'text-gray-500'}>
            {label}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className={`h-2 rounded-full transition-all duration-300 ${color}`}
            style={{ width: `${(level / 5) * 100}%` }}
          />
        </div>
      </div>
      
      {/* Requisitos */}
      {showRequirements && password && (
        <div className="text-xs space-y-1">
          <div className="font-medium text-gray-700">Requisitos:</div>
          <div className="grid grid-cols-1 gap-1">
            <RequirementItem 
              met={password.length >= 8} 
              text="Mínimo 8 caracteres" 
            />
            <RequirementItem 
              met={/[A-Z]/.test(password)} 
              text="Pelo menos uma letra maiúscula" 
            />
            <RequirementItem 
              met={/[a-z]/.test(password)} 
              text="Pelo menos uma letra minúscula" 
            />
            <RequirementItem 
              met={/\d/.test(password)} 
              text="Pelo menos um número" 
            />
            <RequirementItem 
              met={/[!@#$%^&*(),.?":{}|<>]/.test(password)} 
              text="Pelo menos um caractere especial" 
            />
          </div>
        </div>
      )}
      
      {/* Mensagem inicial */}
      {showRequirements && !password && (
        <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
          Digite uma senha forte.
        </div>
      )}
      
      {/* Mensagem de erro */}
      {password && !isStrong && (
        <div className="text-xs text-red-600 bg-red-50 p-2 rounded">
          {getPasswordErrorMessage(password)}
        </div>
      )}
    </div>
  );
}

interface RequirementItemProps {
  met: boolean;
  text: string;
}

function RequirementItem({ met, text }: RequirementItemProps) {
  return (
    <div className="flex items-center gap-2">
      <div className={`w-3 h-3 rounded-full flex items-center justify-center ${
        met ? 'bg-green-500' : 'bg-gray-300'
      }`}>
        {met && (
          <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        )}
      </div>
      <span className={met ? 'text-green-700' : 'text-gray-500'}>
        {text}
      </span>
    </div>
  );
} 